import { Check, LanguageOutlined, OndemandVideo } from "@mui/icons-material";
import { Box, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  AssetType,
  IAsset,
  ILeanAsset,
} from "../../../../../../../types/NendaTypes";
import UrlEmbedBadge from "../../../AssetLibrary/UrlEmbedBadge";

import { clipString } from "../../../../../../utils/string";
import { CustomerPortalState, store } from "../../../../store";
import { selectNavigationScope } from "../../../../store/reducers/workspaceReducer";
import UploadAssetModal from "../../../AssetLibrary/UploadAssetModal";
import { PROMOTION_UPLOAD_REQUEST_ID } from "../../../Premise/Signage/Asset/CreateAssetForm";
import { CreatePromotionFormState } from "../CreateEditPromotionModal";
import { ClearUploadedFiles } from "@client/components/CustomerPortal/store/reducers/companyReducer";

const LIMIT_SELECTION_TO_ONE_ASSET = true;
const DEFAULT_DURATION = 5;

const SelectAssets = ({
  state,
  setState,
  setActiveStep,
  assets,
  isValidStepClick,
}: {
  state: CreatePromotionFormState;
  setState: Dispatch<SetStateAction<CreatePromotionFormState>>;
  setActiveStep: Dispatch<SetStateAction<number>>;
  assets: IAsset[];
  isValidStepClick: (step: number) => boolean;
}) => {
  const assetState = state;
  const theme = useTheme();
  const scope = useSelector(selectNavigationScope);
  const uploadedAssets = useSelector(
    (state: CustomerPortalState) => state.company.uploadedFiles
  );
  const latestUploadedAssetUrl = uploadedAssets[PROMOTION_UPLOAD_REQUEST_ID];
  const latestUploadedAsset = assets.find(
    (a) => a.locator === latestUploadedAssetUrl
  );

  const handleSelect = useCallback(
    (id: string, duration?: number) => {
      if (LIMIT_SELECTION_TO_ONE_ASSET) {
        setState({
          ...assetState,
          media: [{ id, duration: duration || DEFAULT_DURATION }],
        });

        if (assetState.media.length <= 0 && isValidStepClick(1)) {
          setActiveStep(2);
        }
        return;
      }

      const selected = assetState.media.find((m) => m.id === id);
      if (selected) {
        const media = assetState.media.filter((m) => m.id !== id);
        setState({
          ...assetState,
          media,
        });
      } else {
        setState({
          ...assetState,
          media: [...assetState.media, { id, duration: DEFAULT_DURATION }],
        });
      }
    },
    [assetState, isValidStepClick, setActiveStep, setState]
  );

  useEffect(() => {
    if (!latestUploadedAsset?._id) return;

    handleSelect(latestUploadedAsset._id, DEFAULT_DURATION);
    store.dispatch(ClearUploadedFiles(PROMOTION_UPLOAD_REQUEST_ID));
  }, [handleSelect, latestUploadedAsset]);

  // Convert the different assets to the same format
  const items = useCallback(() => {
    type AssetItem = IAsset & { createdAt: Date };
    const assetItems: AssetItem[] = [...assets] as any;
    const sortedAssets = assetItems?.sort((a, b) => {
      const aCreatedAt = a.createdAt;
      const bCreatedAt = b.createdAt;

      return new Date(bCreatedAt).getTime() - new Date(aCreatedAt).getTime();
    });
    return sortedAssets.map((a) => {
      const isUrlAsset = a?.type === AssetType.URL;
      return {
        name: a?.name,
        _id: a?._id,
        // A URL asset's locator is a web page, so only a captured screenshot
        // can stand in as a preview image.
        locator: isUrlAsset
          ? a?.thumbnailLocator
          : a?.thumbnailLocator || a?.locator,
        type: a?.type,
        mimeType: a?.mimeType,
        embedCheck: a?.embedCheck,
      };
    }) as SelectableAssetItem[];
  }, [assets]);

  return (
    <Box sx={{ overflowX: "hidden", width: "100%" }}>
      <Grid
        container
        columns={{ xs: 2, md: 5 }}
        sx={{
          padding: "0.5rem",
          minHeight: "10rem",
          height: "fit-content",
          overflowY: "scroll",
          overflowX: "hidden",
          borderRadius: "0.3rem 0.3rem 0 0",
          width: "100%",
          mb: "1rem",
          outline: `1px solid ${theme.palette.grey[200]}`,
          "&::-webkit-scrollbar": {
            backgroundColor: "transparent",
            position: "absolute",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.transparent.blue,
            borderRadius: "0.5rem",
          },

          [theme.breakpoints.down("md")]: {
            padding: 0,
            minHeight: "initial",
            maxHeight: "30rem",
            overflowY: "initial",
          },
        }}
      >
        {items().map((item, i) => (
          <Grid item xs={1} key={item._id}>
            <AssetItem
              key={item._id + i}
              item={item}
              state={state}
              handleSelect={handleSelect}
              selectedItems={assetState.media.map((m) => m.id)}
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ py: "0.5rem" }}>
        <UploadAssetModal
          company={scope.company || ""}
          premise={scope.premise}
        />
      </Box>
    </Box>
  );
};

type SelectableAssetItem = ILeanAsset & Pick<IAsset, "embedCheck">;

type AssetItemProps = {
  item: SelectableAssetItem;
  state: CreatePromotionFormState;
  handleSelect: (id: string, duration?: number) => void;
  selectedItems?: string[];
  editEnabled?: boolean;
  size?: "small" | "large";
};
const AssetItem = ({
  item,
  handleSelect,
  editEnabled = false,
  state,
}: AssetItemProps) => {
  const theme = useTheme();

  const onSelect = () => {
    handleSelect(item._id, DEFAULT_DURATION);
  };

  const isSelected = state.media.some((m) => m.id === item._id);

  const mimeType = item.type;

  return (
    <Box
      key={"assetItem" + item._id + item.name}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "0.3rem",
        height: "12rem",
        position: "relative",
        gap: "0.5rem",
        p: "0.5rem",
        cursor: "pointer",
        m: "0.5rem",
        outline:
          isSelected && !editEnabled
            ? `4px solid ${theme.palette.primary.light}`
            : "none",
        "&:hover": {
          background: !editEnabled ? theme.palette.grey[100] : "none",
        },
      }}
      onClick={onSelect}
    >
      {isSelected && (
        <Box
          sx={{
            background: theme.palette.transparent.darkBlue,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            inset: 0,
          }}
        >
          <Check sx={{ fill: "white" }} />
        </Box>
      )}
      <Box
        sx={{
          height: "8rem",
          width: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: theme.palette.transparent.blue,
        }}
      >
        {item.locator && item.type !== "video" ? (
          <img
            src={item.locator}
            alt={item.name}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "0.2rem",
              objectFit: "cover",
            }}
          />
        ) : item.type === AssetType.URL ? (
          <LanguageOutlined />
        ) : (
          <OndemandVideo />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          height: "25%",
          width: "100%",
          textOverflow: "clip",
          whiteSpace: "nowrap",
          flexDirection: "column",
          padding: "0.3rem",
        }}
      >
        <Tooltip title={item.name}>
          <Typography variant="caption">{clipString(item.name, 15)}</Typography>
        </Tooltip>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            color: theme.palette.grey[500],
          }}
        >
          <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
            {mimeType}
          </Typography>
          <UrlEmbedBadge embedCheck={item.embedCheck} compact />
        </Box>
      </Box>
    </Box>
  );
};

export default SelectAssets;
